import { inject, Injectable } from '@angular/core';
import { Observable, of, switchMap } from 'rxjs';
import { PRODUCT_TYPES, ServiceType } from '../../models/order/order.model';
import { OrderStateServiceWrapper } from '../../store/state/order/order.state.wrapper.service';
import { OrderApiCacheService } from '../../store/state/order/orderv2/order-api-response/order-api-cache.service';
import { OrderStateServiceV2 } from '../../store/state/order/orderv2/order-state.service';
import { AdditionalInternetOffersStateService } from '../../store/state/order/orderv2/workflows/internet/additional-internet-offers/additional-internet-offers.state.service';
import { InternetFlowStateService } from '../../store/state/order/orderv2/workflows/internet/internet-flow-state.service';
import { InternetPackageSelectionStateService } from '../../store/state/order/orderv2/workflows/internet/internet-package-selection/internet-package-selection-state.service';
import { AdditionalVideoOffersStateService } from '../../store/state/order/orderv2/workflows/video/additional-video-offers/additional-video-offers.state.service';
import { ClosingOffersStateService } from '../../store/state/order/orderv2/workflows/video/closing-offers/closing-offers.state.service';
import { SatelliteHardwareSelectionStateService } from '../../store/state/order/orderv2/workflows/video/satellite-hardware-selection/satellite-hardware-selection.state.service';
import { StreamHardwareSelectionStateService } from '../../store/state/order/orderv2/workflows/video/stream-hardware-selection/stream-hardware-selection.state.service';
import { VideoAdditionalChannelOffersStateService } from '../../store/state/order/orderv2/workflows/video/video-optional-channel-offers/video-optional-channel-offers.state.service';
import { VideoPackageSelectionStateService } from '../../store/state/order/orderv2/workflows/video/video-package-selection/video-package-selection-state.service';
import { VoiceConfigurationStateService } from '../../store/state/order/orderv2/workflows/voice/voice-configuration/voice-configuration-state.service';
import { VoiceFlowStateService } from '../../store/state/order/orderv2/workflows/voice/voice-flow-state.service';
import { VoicePackageSelectionStateService } from '../../store/state/order/orderv2/workflows/voice/voice-package-selection/voice-package-selection-state.service';
import { OrderService } from './order.service';
import { WirelessOrderService } from './wireless-order.service';

/**
 * Removes one product from the order: its order state slices and the gateway's cached PD/UO responses.
 * Mirrors what each package selection page does when its plan is unselected, so a product removed from
 * Seamless Navigation leaves nothing behind in the cart (work item 11648).
 */
@Injectable({ providedIn: 'root' })
export class OrderProductRemovalService {
    private orderStateService = inject(OrderStateServiceWrapper);
    private orderStateServiceV2 = inject(OrderStateServiceV2);
    private orderApiCacheService = inject(OrderApiCacheService);
    private orderService = inject(OrderService);
    private wirelessOrderService = inject(WirelessOrderService);
    private internetPackageSelectionStateService = inject(InternetPackageSelectionStateService);
    private additionalInternetOffersStateService = inject(AdditionalInternetOffersStateService);
    private internetFlowStateService = inject(InternetFlowStateService);
    private videoPackageSelectionStateService = inject(VideoPackageSelectionStateService);
    private additionalVideoOffersStateService = inject(AdditionalVideoOffersStateService);
    private videoAdditionalChannelOffersStateService = inject(VideoAdditionalChannelOffersStateService);
    private streamHardwareSelectionStateService = inject(StreamHardwareSelectionStateService);
    private satelliteHardwareSelectionStateService = inject(SatelliteHardwareSelectionStateService);
    private closingOffersStateService = inject(ClosingOffersStateService);
    private voicePackageSelectionStateService = inject(VoicePackageSelectionStateService);
    private voiceConfigurationStateService = inject(VoiceConfigurationStateService);
    private voiceFlowStateService = inject(VoiceFlowStateService);

    /** Voice depends on Internet, so removing Internet also removes a selected Voice plan (same rule as Internet unselect). */
    removesVoiceWithInternet(): boolean {
        return !!this.orderStateServiceV2.order()?.voiceFlow?.voicePackageSelection?.selectedPlanCode;
    }

    removeProduct(productType: ServiceType): Observable<unknown> {
        switch (productType) {
            case PRODUCT_TYPES.INTERNET:
                return this.removeInternet();
            case PRODUCT_TYPES.VIDEO:
                return this.removeVideo();
            case PRODUCT_TYPES.VOICE:
                return this.removeVoice();
            case PRODUCT_TYPES.WIRELESS:
                return this.removeWireless();
            default:
                return of(null);
        }
    }

    clearInternetState(): void {
        this.orderStateService.setInternetProductDetails(null);
        this.orderStateService.setInternetUpdateOffers(null);
        this.internetPackageSelectionStateService.clear();
        this.additionalInternetOffersStateService.clear();
        this.internetFlowStateService.clear();
    }

    clearVideoState(): void {
        this.orderStateService.setVideoProductDetails(null);
        this.orderStateService.setVideoUpdateOffers(null);
        this.videoPackageSelectionStateService.resetData();
        this.additionalVideoOffersStateService.resetData();
        this.videoAdditionalChannelOffersStateService.resetData();
        this.streamHardwareSelectionStateService.resetData();
        this.satelliteHardwareSelectionStateService.resetData();
        this.closingOffersStateService.resetData();
    }

    clearVoiceState(): void {
        this.orderStateService.setVoiceProductDetails(null);
        this.voiceConfigurationStateService.clear();
        this.voicePackageSelectionStateService.clear();
        this.voiceFlowStateService.clear();
    }

    private removeInternet(): Observable<unknown> {
        const removeVoice = this.removesVoiceWithInternet();
        this.clearInternetState();
        if (removeVoice) {
            this.clearVoiceState();
        }
        const productTypes = removeVoice
            ? ['INTERNET_PD', 'VOICE_PD', 'PD', 'INTERNET_UO']
            : ['INTERNET_PD', 'PD', 'INTERNET_UO'];
        return this.orderApiCacheService.removeCacheDataByOrder(this.orderStateService.getOrder(), productTypes);
    }

    private removeVideo(): Observable<unknown> {
        this.clearVideoState();
        return this.orderApiCacheService.removeCacheDataByOrder(this.orderStateService.getOrder(), ['VIDEO_PD', 'VIDEO_UO']);
    }

    private removeVoice(): Observable<unknown> {
        this.clearVoiceState();
        return this.orderApiCacheService.removeCacheDataByOrder(this.orderStateService.getOrder(), ['VOICE_PD', 'PD', 'INTERNET_UO']);
    }

    private removeWireless(): Observable<unknown> {
        return this.orderService.getWirelessProductDetailsBeforeRemoval().pipe(
            switchMap(() => this.wirelessOrderService.removeWirelessProduct())
        );
    }
}
